/**
 * Shared helpers for exporting a server-side (SSRM) grid's full filtered result set to CSV.
 *
 * AG Grid's `api.exportDataAsCsv()` only serializes the row blocks fetched so far, so it is
 * insufficient for SSRM grids where the filtered set spans many pages. These helpers let a grid
 * fetch every page that matches the current filter/sort (reusing the datasource's query context)
 * and serialize the rows through the grid's own column definitions / valueGetters.
 */

/**
 * The normalized result of fetching one page of rows for the fetch-all loop.
 */
export type FetchPageResult<TItem> = {
  /** Rows returned for this page (may be expanded display rows, see `sourceCount`). */
  rows: TItem[];
  /**
   * Total number of rows in the filtered set, when the API provides one.
   * Used as an early-stop hint; the loop also stops on an empty page or the max-row cap.
   */
  total?: number;
  /**
   * Number of *source* rows the server returned for this page, when the caller knows it.
   * Offset/limit pagination is over source rows, so the loop advances the offset by this value
   * (falling back to the returned row count) — this keeps pagination correct when a datasource
   * expands a page into more display rows than source records (e.g. the tooling grid's unassigned
   * row) or when a server caps `limit` below the requested page size.
   */
  sourceCount?: number;
};

/**
 * Options for {@link fetchAllPages}.
 */
export type FetchAllPagesOptions<TItem> = {
  /**
   * Fetches a single page of the filtered set at the given `offset` / `limit`.
   * The caller is responsible for reusing the datasource's query context (filter/sort/URL state).
   */
  fetchPage: (offset: number, limit: number) => Promise<FetchPageResult<TItem>>;
  /** Rows to request per page. Defaults to 100. */
  pageSize?: number;
  /**
   * Optional safety cap on how many rows are collected in client memory. When omitted (the
   * default) the full filtered set is exported — the ticket's requirement; when set, collection
   * stops once the cap is reached and only that many rows are returned.
   */
  maxRows?: number;
};

/**
 * Fetches every page of a filtered result set by looping `offset` / `limit` until the set is
 * exhausted (empty page) or the reported total is reached, or — when `maxRows` is set — the cap
 * is hit. By default there is no cap, so the full filtered list is always exported.
 *
 * Pages are requested with a fixed `pageSize`. The offset advances by the number of *source*
 * rows the server consumed for the page — `sourceCount` when the caller reports it, otherwise the
 * returned row count — capped at `pageSize`. This keeps pagination correct when a server caps
 * `limit` below the requested page size (advancing by `pageSize` would skip rows), and when a
 * datasource expands a page into more display rows than source records (e.g. the tooling grid's
 * unassigned row) the offset does not jump past un-fetched source rows.
 */
export async function fetchAllPages<TItem>({
  fetchPage,
  pageSize = 100,
  maxRows,
}: FetchAllPagesOptions<TItem>): Promise<TItem[]> {
  const pageLimit = Math.max(1, pageSize);
  const rowCap = typeof maxRows === 'number' ? Math.max(1, maxRows) : Infinity;
  const all: TItem[] = [];
  let offset = 0;
  let total: number | undefined;

  for (;;) {
    const { rows, total: pageTotal, sourceCount: pageSourceCount } = await fetchPage(offset, pageLimit);
    if (typeof pageTotal === 'number') {
      total = pageTotal;
    }
    if (rows.length === 0) {
      break;
    }
    all.push(...rows);
    // Advance by the source rows consumed (falls back to the returned row count), capped at the
    // requested page size so a server-capped page still advances and an expanded page does not jump.
    const consumed =
      typeof pageSourceCount === 'number' && pageSourceCount > 0 ? pageSourceCount : rows.length;
    offset += Math.min(consumed, pageLimit);
    if (all.length >= rowCap) {
      break;
    }
    if (typeof total === 'number' && offset >= total) {
      break;
    }
  }

  return all.slice(0, rowCap);
}

/**
 * A minimal column shape understood by {@link buildCsv}. AG Grid `ColDef`s satisfy this:
 * `valueGetter` is declared as a function here so that a `ColDef` (whose `valueGetter` may also
 * be a `string` / `boolean` shorthand) is assignable, while {@link buildCsv} only invokes it when
 * it is actually a function.
 */
export type CsvColumnDef = {
  colId?: string;
  headerName?: string;
  field?: string;
  valueGetter?: ((params: any) => unknown) | string | boolean;
  /**
   * Optional tooltip text for the column. A def that carries it is expanded by {@link buildCsv}
   * into one extra "<Header> (Tooltip)" column, so detail that is only visible in a hover tooltip
   * still reaches the export.
   */
  tooltipExport?: CsvTooltipExport;
  /**
   * AG Grid's sanctioned annotation slot for application data on a colDef (AG Grid warns about any
   * other unknown colDef property), so a grid whose defs are handed to the grid itself normally
   * declares its {@link CsvColumnDef.tooltipExport} here instead of at the top level. Both are read.
   */
  context?: { tooltipExport?: CsvTooltipExport };
};

/**
 * Resolves the tooltip text of one cell. Called with the row data and the grid `context`, like a
 * `valueGetter`, so tooltip helpers that read `params.context` (providers, connectors) work.
 */
export type CsvTooltipExport = {
  valueGetter: (params: { data: any; node: { data: any }; context: unknown }) => unknown;
};

/** Header suffix of the extra column generated from a column's `tooltipExport`. */
export const TOOLTIP_HEADER_SUFFIX = ' (Tooltip)';

/**
 * Separators used when several tooltip values share one CSV cell: separate groups (one per
 * provider / asset) are joined with `TOOLTIP_VALUE_SEPARATOR`, the label/value pairs within one
 * group with `TOOLTIP_FIELD_SEPARATOR`.
 */
export const TOOLTIP_VALUE_SEPARATOR = ' | ';
export const TOOLTIP_FIELD_SEPARATOR = '; ';

/** A label/value pair as the grid tooltips render them (see ToolingCoverageTooltip.Row). */
export type CsvTooltipField = {
  label: string;
  value?: string;
};

/**
 * The tooltip rows of one item: a label naming the item (provider, asset, ...) followed by its
 * label/value rows. A cell with several items (assets, MFA providers) holds several groups.
 */
export type CsvTooltipGroup = {
  label: string;
  fields: ReadonlyArray<CsvTooltipField>;
};

/**
 * Renders tooltip fields as readable text: `Label: value` pairs joined with `separator`. A field
 * without a value keeps its label only, mirroring a tooltip row that renders an empty value.
 */
export function formatCsvTooltipFields(
  fields: ReadonlyArray<CsvTooltipField>,
  separator: string = TOOLTIP_FIELD_SEPARATOR,
): string {
  return fields
    .map((field) => (field.value ? `${field.label}: ${field.value}` : field.label))
    .filter((text) => text.length > 0)
    .join(separator);
}

/**
 * Renders one tooltip group per line-item (provider / asset / SaaS asset) and joins the groups, so
 * cells with multiple items read consistently: `Okta; Connector Status: ACTIVE | Google; ...`.
 */
export function formatCsvTooltipGroups(groups: ReadonlyArray<CsvTooltipGroup>): string {
  return groups
    .map((group) => formatCsvTooltipFields([{ label: group.label }, ...group.fields]))
    .filter((text) => text.length > 0)
    .join(TOOLTIP_VALUE_SEPARATOR);
}

/**
 * Escapes a single CSV cell: wraps the value in double quotes and doubles any embedded quotes.
 * `null` / `undefined` become an empty cell.
 */
function escapeCsvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Reads a property off an object; if the property is a function it is invoked (AG Grid allows
 * string `valueGetter` / `valueFormatter` values to name a getter function on the data).
 */
function readPropertyPath(obj: unknown, path: string): unknown {
  const value = (obj as Record<string, unknown> | null)?.[path];
  return typeof value === 'function' ? (value as () => unknown)() : value;
}

/**
 * Resolves the value of a single cell for a row, mirroring AG Grid's own resolution order:
 * a function `valueGetter` (invoked with the grid `context`), a string `valueGetter` (property
 * path), or the row's `field` / `colId`. The result is then passed through the column's
 * `valueFormatter` (function or string property path) when present, so exported values match what
 * the grid displays (e.g. status labels, formatted dates).
 */
function resolveCellValue(col: any, row: unknown, context: unknown): unknown {
  const params = { data: row, node: { data: row }, context };
  let raw: unknown;
  if (typeof col?.valueGetter === 'function') {
    raw = col.valueGetter(params);
  } else if (typeof col?.valueGetter === 'string') {
    raw = readPropertyPath(row, col.valueGetter);
  } else if (col?.field) {
    raw = (row as Record<string, unknown>)[col.field];
  } else if (col?.colId) {
    raw = (row as Record<string, unknown>)[col.colId];
  } else {
    raw = undefined;
  }

  const formatter = col?.valueFormatter;
  if (typeof formatter === 'function') {
    return formatter({ ...params, value: raw });
  }
  if (typeof formatter === 'string') {
    // A string valueFormatter is a property path on the row data (AG Grid semantics).
    return readPropertyPath(row, formatter);
  }
  return raw;
}

/**
 * Expands every column that carries a `tooltipExport` into itself plus one extra column holding the
 * tooltip text, titled "<Header> (Tooltip)". Columns without a `tooltipExport` are returned
 * unchanged, so a grid whose defs never use the option exports exactly what it exported before.
 *
 * The extra column reuses the source column's header (falling back to `colId` / `field`), so the
 * tooltip value sits next to the visible value it describes.
 */
export function tooltipExportColumns(columns: ReadonlyArray<CsvColumnDef>): CsvColumnDef[] {
  const expanded: CsvColumnDef[] = [];
  columns.forEach((col) => {
    expanded.push(col);
    const tooltipExport = col?.tooltipExport ?? col?.context?.tooltipExport;
    if (!tooltipExport) {
      return;
    }
    const header = col.headerName ?? col.colId ?? col.field ?? '';
    expanded.push({
      colId: `${col.colId ?? col.field ?? header}${TOOLTIP_HEADER_SUFFIX}`,
      headerName: `${header}${TOOLTIP_HEADER_SUFFIX}`,
      valueGetter: tooltipExport.valueGetter,
    });
  });
  return expanded;
}

/**
 * Serializes rows to a CSV string, mapping each column through its `valueGetter` / `valueFormatter`
 * with the provided grid `context`, otherwise falling back to the row's `field` / `colId`.
 *
 * Columns appear in the order of the provided `columns` array. A column whose def carries a
 * `tooltipExport` contributes a second "<Header> (Tooltip)" column immediately after it, resolved
 * with the same grid `context`.
 */
export function buildCsv(
  rows: unknown[],
  columns: ReadonlyArray<any>,
  context?: unknown,
): string {
  const exportColumns = tooltipExportColumns(columns);
  const header = exportColumns.map((col) => escapeCsvCell(col.headerName ?? col.colId ?? col.field ?? '')).join(',');
  const body = rows.map((row) =>
    exportColumns
      .map((col) => escapeCsvCell(resolveCellValue(col, row, context)))
      .join(','),
  );
  return [header, ...body].join('\n');
}

/**
 * Resolves the column definitions that should be exported, in the grid's current display order,
 * so hidden columns are omitted and column ordering matches what the user sees in the UI.
 *
 * Reads the live column state from the grid API (falling back to the static `columnDefs` when the
 * API is unavailable or not yet ready) and reorders / filters by `colId`.
 */
export function visibleColumnDefs(api: any, columnDefs: ReadonlyArray<any>): ReadonlyArray<any> {
  const state: any[] = typeof api?.getColumnState === 'function' ? api.getColumnState() : [];
  if (!Array.isArray(state) || state.length === 0) {
    return columnDefs;
  }
  const defsById = new Map<string, any>();
  columnDefs.forEach((col) => {
    const id = col?.colId ?? col?.field;
    if (id && !defsById.has(id)) {
      defsById.set(id, col);
    }
  });
  const visible: any[] = [];
  state.forEach((entry) => {
    // AG Grid's ColumnState reports hidden columns as `hide: true`.
    if (!entry || entry.hide === true) {
      return;
    }
    const def = defsById.get(entry.colId);
    if (def && !visible.includes(def)) {
      visible.push(def);
    }
  });
  return visible.length > 0 ? visible : columnDefs;
}

/**
 * Triggers a browser download of the given CSV content under `filename`.
 */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
